import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Check, Crown, Loader2, Sparkles } from "lucide-react";
import { createCheckout } from "../lib/api";
import { toast } from "sonner";

const FEATURES = [
  "100 deep-grid generations / month",
  "Unlimited note uploads (PDF, DOCX, images)",
  "Interactive flashcard study viewer",
  "Full Master Anki Tree export (.apkg)",
  "Save & share unlimited concept maps",
];

const PLANS = {
  monthly: { price: "$12.99", period: "/ month", note: "" },
  yearly: { price: "$124.99", period: "/ year", note: "Save 20% — 2 months free" },
};

export default function UpgradeDialog({ open, onOpenChange, reason }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("yearly");

  const upgrade = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(plan);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  const p = PLANS[plan];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-dialog" className="max-w-md bg-[#0D0B18] border border-purple-500/30 text-white">
        <DialogTitle className="sr-only">Go Premium</DialogTitle>
        <DialogDescription className="sr-only">Choose a plan and upgrade to Premium.</DialogDescription>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-amber-400/20 to-purple-600/20 border border-amber-300/30">
            <Crown className="w-5 h-5 text-amber-300" />
          </div>
          <h3 className="font-display text-xl font-bold text-white">Go Premium</h3>
        </div>

        {reason && (
          <p data-testid="upgrade-reason" className="text-sm text-amber-300/90 bg-amber-500/10 border border-amber-400/20 rounded-lg px-3 py-2">
            {reason}
          </p>
        )}

        {/* Plan toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[#07070E] border border-purple-500/20">
          <button data-testid="plan-monthly-btn" onClick={() => setPlan("monthly")}
            className={"rounded-lg py-2 text-sm font-semibold transition-colors " +
              (plan === "monthly" ? "bg-purple-600 text-white" : "text-purple-200/70 hover:text-white")}>
            Monthly
          </button>
          <button data-testid="plan-yearly-btn" onClick={() => setPlan("yearly")}
            className={"rounded-lg py-2 text-sm font-semibold transition-colors relative " +
              (plan === "yearly" ? "bg-purple-600 text-white" : "text-purple-200/70 hover:text-white")}>
            Yearly
            <span className="absolute -top-2 -right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">-20%</span>
          </button>
        </div>

        <div className="flex items-baseline gap-1 mt-1">
          <span data-testid="upgrade-price" className="font-display text-4xl font-extrabold text-white">{p.price}</span>
          <span className="text-purple-300/70">{p.period}</span>
        </div>
        {p.note && <p className="text-xs text-emerald-400 -mt-1">{p.note}</p>}

        <ul className="space-y-2 mt-2">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-purple-100">
              <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <Button data-testid="upgrade-checkout-btn" onClick={upgrade} disabled={loading}
          className="w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow-[0_0_24px_rgba(168,85,247,0.5)] mt-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Upgrade — {p.price}{p.period}</>}
        </Button>
        <p className="text-center text-xs text-purple-300/40">Test card: 4242 4242 4242 4242 · any future date · any CVC</p>
      </DialogContent>
    </Dialog>
  );
}
